import { Node, Edge } from 'reactflow';
import { nodeDefinitions } from '@/utils/nodeDefinitions';
import { getNodeDefinition } from '@/lib/node-registry';
import { type LucideIcon } from 'lucide-react';

export interface NavigationTarget {
  node: Node | null;
  distance: number; // How many nodes away (1 = direct connection)
}

export class NodeNavigationService {
  /**
   * Check if a node is disabled
   */
  private static isNodeDisabled(node: Node | null): boolean {
    if (!node) return false;
    return node.data?.disabled || node.data?.settings?.disabled || false;
  }

  /**
   * Sub-node anchor handles used by AI Agent nodes
   */
  static readonly SUB_NODE_HANDLES = ['chatModel', 'memory', 'tools', 'parser'] as const;

  /**
   * Check if an edge is a sub-node connection (anchor connection)
   */
  private static isSubNodeEdge(edge: Edge): boolean {
    // Sub-node edges have specific sourceHandles (chatModel, memory, tools, parser)
    // and targetHandle of "subNodeInput"
    return (
      (edge.sourceHandle && this.SUB_NODE_HANDLES.includes(edge.sourceHandle as any)) ||
      edge.targetHandle === 'subNodeInput'
    );
  }

  /**
   * Check if a node is a sub-node based on edges
   */
  static isSubNode(nodeId: string, edges: Edge[]): boolean {
    // A node is a sub-node if it's connected via an anchor edge as the target
    return edges.some(
      edge => edge.target === nodeId && this.isSubNodeEdge(edge)
    );
  }

  /**
   * Get the upstream node (single primary input)
   * Returns the node that feeds into the current node
   * Excludes sub-node connections when not on a sub-node
   * Skips disabled nodes and finds the previous enabled node in the path
   */
  static getUpstreamNode(
    currentNodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): Node | null {
    const isCurrentNodeSubNode = this.isSubNode(currentNodeId, edges);
    const visited = new Set<string>();
    let current = currentNodeId;

    // Traverse upstream until we find an enabled node or reach the start
    while (current) {
      // Prevent infinite loops
      if (visited.has(current)) {
        return null;
      }
      visited.add(current);

      // Find edge where target = current node
      // If current node is NOT a sub-node, exclude anchor edges
      const incomingEdge = edges.find(edge => {
        if (edge.target !== current) return false;

        // If we're on a sub-node, allow all edges
        if (isCurrentNodeSubNode) return true;

        // If we're on a main node, exclude sub-node edges
        return !this.isSubNodeEdge(edge);
      });

      if (!incomingEdge) {
        return null;
      }

      // Get the source node
      const sourceNode = nodes.find(node => node.id === incomingEdge.source);

      if (!sourceNode) {
        return null;
      }

      // If the node is enabled, return it
      if (!this.isNodeDisabled(sourceNode)) {
        return sourceNode;
      }

      // If the node is disabled, continue to the previous node
      current = sourceNode.id;
    }

    return null;
  }

  /**
   * Get the downstream node (single primary output)
   * Returns the node that receives output from the current node
   * Excludes sub-node connections when not on a sub-node
   * Skips disabled nodes and finds the next enabled node in the path
   */
  static getDownstreamNode(
    currentNodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): Node | null {
    const isCurrentNodeSubNode = this.isSubNode(currentNodeId, edges);
    const visited = new Set<string>();
    let current = currentNodeId;

    // Traverse downstream until we find an enabled node or reach the end
    while (current) {
      // Prevent infinite loops
      if (visited.has(current)) {
        return null;
      }
      visited.add(current);

      // Find edge where source = current node
      // If current node is NOT a sub-node, exclude anchor edges
      const outgoingEdge = edges.find(edge => {
        if (edge.source !== current) return false;

        // If we're on a sub-node, allow all edges
        if (isCurrentNodeSubNode) return true;

        // If we're on a main node, exclude sub-node edges
        return !this.isSubNodeEdge(edge);
      });

      if (!outgoingEdge) {
        return null;
      }

      // Get the target node
      const targetNode = nodes.find(node => node.id === outgoingEdge.target);

      if (!targetNode) {
        return null;
      }

      // If the node is enabled, return it
      if (!this.isNodeDisabled(targetNode)) {
        return targetNode;
      }

      // If the node is disabled, continue to the next node
      current = targetNode.id;
    }

    return null;
  }

  /**
   * Get all upstream nodes (for nodes with multiple inputs)
   */
  static getAllUpstreamNodes(
    currentNodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): Node[] {
    const incomingEdges = edges.filter(edge => edge.target === currentNodeId);
    
    return incomingEdges
      .map(edge => nodes.find(node => node.id === edge.source))
      .filter((node): node is Node => node !== undefined);
  }

  /**
   * Get all downstream nodes (for nodes with multiple outputs)
   */
  static getAllDownstreamNodes(
    currentNodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): Node[] {
    const outgoingEdges = edges.filter(edge => edge.source === currentNodeId);
    
    return outgoingEdges
      .map(edge => nodes.find(node => node.id === edge.target))
      .filter((node): node is Node => node !== undefined);
  }

  /**
   * Get all downstream nodes grouped by output handle
   * Returns an array of { handle: string, handleLabel: string, node: Node }
   * Excludes sub-node connections when not on a sub-node
   * Skips disabled nodes and finds the next enabled node in each path
   */
  static getDownstreamNodesByHandle(
    currentNodeId: string,
    nodes: Node[],
    edges: any[]
  ): Array<{ handle: string; handleLabel: string; node: Node }> {
    const currentNode = nodes.find(n => n.id === currentNodeId);
    if (!currentNode) return [];

    const isCurrentNodeSubNode = this.isSubNode(currentNodeId, edges);

    // Get full node definition from registry to find output handle labels
    const nodeType = currentNode.type || 'default';
    const fullNodeDef = getNodeDefinition(nodeType);

    // Find all outgoing edges, excluding sub-node edges if we're on a main node
    const outgoingEdges = edges.filter(edge => {
      if (edge.source !== currentNodeId) return false;

      // If we're on a sub-node, allow all edges
      if (isCurrentNodeSubNode) return true;

      // If we're on a main node, exclude sub-node edges
      return !this.isSubNodeEdge(edge);
    });

    // Group by sourceHandle and find connected nodes
    const result: Array<{ handle: string; handleLabel: string; node: Node }> = [];
    const seenTargets = new Set<string>(); // Track seen target nodes to avoid duplicates

    for (const edge of outgoingEdges) {
      const handleId = edge.sourceHandle || 'default';

      // Find the output definition to get the display name
      const outputDef = fullNodeDef?.outputs?.find(o => o.name === handleId);
      const handleLabel = outputDef?.displayName || handleId;

      // Find the next enabled node in this path
      const enabledNode = this.findNextEnabledNode(edge.target, nodes, edges, isCurrentNodeSubNode);

      if (!enabledNode) continue;

      // Skip if we've already added this target node (avoid duplicate navigation buttons)
      if (seenTargets.has(enabledNode.id)) continue;
      seenTargets.add(enabledNode.id);

      result.push({
        handle: handleId,
        handleLabel,
        node: enabledNode,
      });
    }

    return result;
  }

  /**
   * Helper method to find the next enabled node in a path
   * Recursively traverses downstream until an enabled node is found
   */
  private static findNextEnabledNode(
    startNodeId: string,
    nodes: Node[],
    edges: any[],
    isSubNodeContext: boolean
  ): Node | null {
    const visited = new Set<string>();
    let current = startNodeId;

    while (current) {
      // Prevent infinite loops
      if (visited.has(current)) {
        return null;
      }
      visited.add(current);

      const currentNode = nodes.find(n => n.id === current);
      if (!currentNode) {
        return null;
      }

      // If this node is enabled, return it
      if (!this.isNodeDisabled(currentNode)) {
        return currentNode;
      }

      // Node is disabled, find the next node in the path
      const outgoingEdge = edges.find(edge => {
        if (edge.source !== current) return false;

        // If we're in sub-node context, allow all edges
        if (isSubNodeContext) return true;

        // Otherwise, exclude sub-node edges
        return !this.isSubNodeEdge(edge);
      });

      if (!outgoingEdge) {
        return null;
      }

      current = outgoingEdge.target;
    }

    return null;
  }

  /**
   * Get node metadata (icon, label, type)
   */
  static getNodeMetadata(node: Node | null) {
    if (!node) {
      return null;
    }

    // Look up node definition to get icon
    const nodeType = node.type || 'default';
    const nodeDef = nodeDefinitions[nodeType];

    return {
      id: node.id,
      type: nodeType,
      label: node.data?.label || nodeDef?.label || 'Unnamed Node',
      icon: nodeDef?.icon,
      color: nodeDef?.color || node.data?.color,
      bgColor: nodeDef?.bgColor,
    };
  }

  /**
   * Check if navigation is possible in a direction
   */
  static canNavigate(
    currentNodeId: string,
    direction: 'upstream' | 'downstream',
    nodes: Node[],
    edges: Edge[]
  ): boolean {
    if (direction === 'upstream') {
      return this.getUpstreamNode(currentNodeId, nodes, edges) !== null;
    } else {
      return this.getDownstreamNode(currentNodeId, nodes, edges) !== null;
    }
  }

  /**
   * Get navigation path (for breadcrumb or multi-hop navigation)
   */
  static getNavigationPath(
    startNodeId: string,
    endNodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): Node[] {
    const path: Node[] = [];
    const visited = new Set<string>();
    
    const findPath = (currentId: string): boolean => {
      if (currentId === endNodeId) {
        const node = nodes.find(n => n.id === currentId);
        if (node) path.push(node);
        return true;
      }
      
      if (visited.has(currentId)) {
        return false;
      }
      
      visited.add(currentId);
      const currentNode = nodes.find(n => n.id === currentId);
      if (!currentNode) return false;
      
      // Try downstream first
      const downstream = this.getDownstreamNode(currentId, nodes, edges);
      if (downstream && findPath(downstream.id)) {
        path.unshift(currentNode);
        return true;
      }
      
      return false;
    };
    
    findPath(startNodeId);
    return path;
  }

  /**
   * Get the parent AI Agent node for a sub-node
   * Returns null if the node is not a sub-node
   */
  static getParentAgentNode(
    nodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): { node: Node; handleType: string } | null {
    // Find the edge where this node is the target and it's a sub-node edge
    const subNodeEdge = edges.find(
      edge => edge.target === nodeId && this.isSubNodeEdge(edge)
    );

    if (!subNodeEdge) {
      return null;
    }

    const parentNode = nodes.find(n => n.id === subNodeEdge.source);
    if (!parentNode) {
      return null;
    }

    return {
      node: parentNode,
      handleType: subNodeEdge.sourceHandle || 'unknown'
    };
  }

  /**
   * Get all connected sub-nodes for an AI Agent node
   * Returns an array of { handleType, node } for each connected sub-node
   */
  static getConnectedSubNodes(
    nodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): Array<{ handleType: string; node: Node }> {
    const result: Array<{ handleType: string; node: Node }> = [];

    // Find all edges where this node is the source and it's a sub-node edge
    const subNodeEdges = edges.filter(
      edge => edge.source === nodeId && this.isSubNodeEdge(edge)
    );

    for (const edge of subNodeEdges) {
      const subNode = nodes.find(n => n.id === edge.target);
      if (subNode) {
        result.push({
          handleType: edge.sourceHandle || 'unknown',
          node: subNode
        });
      }
    }

    return result;
  }
}
